import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Area, AreaChart
} from 'recharts';
import {
  Activity, Clock, Calendar, TrendingUp, Sparkles, 
  Compass, Sun, Coffee, ChevronRight, Star, Zap, Award, Trophy, Medal
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Confetti from 'react-confetti';

const API = process.env.REACT_APP_BACKEND_URL;

// Colors for charts
const COLORS = ['#00D3A8', '#00BCD4', '#4CAF50', '#FF9800', '#9C27B0', '#E91E63', '#3F51B5'];
const CATEGORY_COLORS = {
  'Finance': '#4CAF50',
  'Communication': '#2196F3',
  'Gestion': '#FF9800',
  'Réunions': '#9C27B0',
  'Patrimoine': '#00BCD4',
  'Personnes': '#E91E63',
  'Documents': '#795548',
  'Analyses': '#607D8B',
  'Participation': '#FFC107',
  'Navigation': '#00D3A8',
  'Autre': '#9E9E9E'
};

const LEVEL_COLORS = {
  platinum: 'from-purple-400 to-purple-600',
  gold: 'from-yellow-400 to-yellow-600',
  silver: 'from-gray-300 to-gray-500',
  bronze: 'from-orange-300 to-orange-500'
};

const NavigationStats = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [heatmap, setHeatmap] = useState(null);
  const [badges, setBadges] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [newBadges, setNewBadges] = useState([]);
  const [showConfetti, setShowConfetti] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (token) {
      fetchAllData();
    }
  }, [token]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statsRes, suggestionsRes, heatmapRes, badgesRes, leaderboardRes] = await Promise.all([
        axios.get(`${API}/api/analytics/pages/detailed-stats`, { headers }),
        axios.get(`${API}/api/analytics/pages/suggestions`, { headers }),
        axios.get(`${API}/api/analytics/pages/activity-heatmap`, { headers }),
        axios.get(`${API}/api/analytics/pages/badges`, { headers }),
        axios.get(`${API}/api/analytics/pages/leaderboard`, { headers })
      ]);
      
      setStats(statsRes.data);
      setSuggestions(suggestionsRes.data.suggestions || []);
      setHeatmap(heatmapRes.data);
      setBadges(badgesRes.data);
      setLeaderboard(leaderboardRes.data);
      
      // Show confetti if new badges earned
      if (badgesRes.data.new_badges?.length > 0) {
        setNewBadges(badgesRes.data.new_badges);
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 5000);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSuggestionIcon = (iconName) => {
    switch (iconName) {
      case 'sparkles': return <Sparkles className="text-yellow-500" size={20} />;
      case 'compass': return <Compass className="text-blue-500" size={20} />;
      case 'sun': return <Sun className="text-orange-400" size={20} />;
      case 'coffee': return <Coffee className="text-amber-600" size={20} />;
      default: return <Star className="text-purple-500" size={20} />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'border-l-green-500 bg-green-50';
      case 'medium': return 'border-l-blue-500 bg-blue-50';
      default: return 'border-l-gray-300 bg-gray-50';
    }
  };

  // Heatmap cell component
  const HeatmapCell = ({ value, max }) => {
    const intensity = max > 0 ? value / max : 0;
    const bgColor = `rgba(0, 211, 168, ${Math.max(0.1, intensity)})`;
    return (
      <div 
        className="w-6 h-6 rounded-sm flex items-center justify-center text-[8px] font-medium"
        style={{ backgroundColor: bgColor, color: intensity > 0.5 ? 'white' : '#666' }}
        title={`${value} visites`}
      >
        {value > 0 ? value : ''}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00D3A8]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="navigation-stats">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mes Statistiques</h1>
          <p className="text-gray-500 mt-1">Analysez vos habitudes de navigation</p>
        </div>
        <div className="flex items-center gap-2 bg-[#00D3A8]/10 px-4 py-2 rounded-lg">
          <Activity className="text-[#00D3A8]" size={20} />
          <span className="text-[#00D3A8] font-semibold">
            {stats?.summary?.recent_activity_7d || 0} actions cette semaine
          </span>
        </div>
      </div>

      {/* Confetti for new badges */}
      {showConfetti && <Confetti recycle={false} numberOfPieces={200} />}
      
      {/* New Badge Popup */}
      {newBadges.length > 0 && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setNewBadges([])}>
          <div className="bg-white rounded-2xl p-8 max-w-md text-center animate-bounce-in" onClick={e => e.stopPropagation()}>
            <div className="text-6xl mb-4">{newBadges[0].icon}</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Nouveau Badge !</h2>
            <h3 className="text-xl font-semibold text-[#00D3A8] mb-2">{newBadges[0].name}</h3>
            <p className="text-gray-600 mb-4">{newBadges[0].description}</p>
            <div className="flex items-center justify-center gap-2 text-yellow-500 mb-4">
              <Zap size={20} />
              <span className="font-bold">+{newBadges[0].xp} XP</span>
            </div>
            <button 
              onClick={() => setNewBadges([])}
              className="bg-[#00D3A8] text-white px-6 py-2 rounded-lg hover:bg-[#00BFA5] transition-colors"
            >
              Super !
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b overflow-x-auto">
        {[
          { id: 'overview', label: 'Vue d\'ensemble', icon: TrendingUp },
          { id: 'badges', label: 'Badges', icon: Award },
          { id: 'leaderboard', label: 'Classement', icon: Trophy },
          { id: 'time', label: 'Analyse temporelle', icon: Clock },
          { id: 'suggestions', label: 'Suggestions', icon: Sparkles }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-all whitespace-nowrap ${
              activeTab === tab.id 
                ? 'border-[#00D3A8] text-[#00D3A8]' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon size={18} />
            {tab.label}
            {tab.id === 'badges' && badges && (
              <span className="ml-1 text-xs bg-[#00D3A8]/20 text-[#00D3A8] px-2 py-0.5 rounded-full">
                {badges.earned_count}/{badges.total_count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white p-4 rounded-xl shadow-sm border">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#00D3A8]/10 rounded-lg">
                  <Activity className="text-[#00D3A8]" size={24} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats?.summary?.total_visits || 0}</p>
                  <p className="text-sm text-gray-500">Visites totales</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-4 rounded-xl shadow-sm border">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Compass className="text-blue-500" size={24} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats?.summary?.total_pages || 0}</p>
                  <p className="text-sm text-gray-500">Pages explorées</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-4 rounded-xl shadow-sm border">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Calendar className="text-purple-500" size={24} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats?.summary?.avg_daily_visits || 0}</p>
                  <p className="text-sm text-gray-500">Visites/jour (7j)</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-4 rounded-xl shadow-sm border">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Zap className="text-orange-500" size={24} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats?.peak_hours?.[0]?.label || '-'}
                  </p>
                  <p className="text-sm text-gray-500">Heure de pointe</p>
                </div>
              </div>
            </div>
          </div>

          {/* Category Breakdown Pie Chart */}
          <div className="bg-white p-6 rounded-xl shadow-sm border">
            <h3 className="font-semibold text-gray-900 mb-4">Répartition par catégorie</h3>
            {stats?.category_breakdown?.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={stats.category_breakdown}
                    dataKey="visits"
                    nameKey="category"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ category, percent }) => `${category} ${(percent * 100).toFixed(0)}%`}
                  >
                    {stats.category_breakdown.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={CATEGORY_COLORS[entry.category] || COLORS[index % COLORS.length]} 
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-gray-400">
                Pas encore de données
              </div>
            )}
          </div>

          {/* Top Pages */}
          <div className="bg-white p-6 rounded-xl shadow-sm border lg:col-span-2">
            <h3 className="font-semibold text-gray-900 mb-4">Pages les plus visitées</h3>
            {stats?.top_pages?.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={stats.top_pages} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" />
                  <YAxis 
                    type="category" 
                    dataKey="page_name" 
                    width={120}
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip />
                  <Bar dataKey="visit_count" fill="#00D3A8" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-gray-400">
                Commencez à naviguer pour voir vos statistiques
              </div>
            )}
          </div>
        </div>
      )}

      {/* Time Analysis Tab */}
      {activeTab === 'time' && (
        <div className="space-y-6">
          {/* Hourly Distribution */}
          <div className="bg-white p-6 rounded-xl shadow-sm border">
            <h3 className="font-semibold text-gray-900 mb-4">Distribution horaire</h3>
            {stats?.hourly_distribution ? (
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={stats.hourly_distribution}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                  <YAxis />
                  <Tooltip />
                  <Area 
                    type="monotone" 
                    dataKey="visits" 
                    stroke="#00D3A8" 
                    fill="#00D3A8" 
                    fillOpacity={0.3}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-gray-400">
                Pas encore de données temporelles
              </div>
            )}
          </div>

          {/* Daily Distribution */}
          <div className="bg-white p-6 rounded-xl shadow-sm border">
            <h3 className="font-semibold text-gray-900 mb-4">Activité par jour de la semaine</h3>
            {stats?.daily_breakdown ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={stats.daily_breakdown}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="visits" fill="#00BCD4" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : null}
          </div>

          {/* Activity Heatmap */}
          <div className="bg-white p-6 rounded-xl shadow-sm border">
            <h3 className="font-semibold text-gray-900 mb-4">Carte de chaleur d'activité</h3>
            <p className="text-sm text-gray-500 mb-4">Heures × Jours (30 derniers jours)</p>
            {heatmap?.heatmap ? (
              <div className="overflow-x-auto">
                <div className="min-w-[600px]">
                  {/* Hours header */}
                  <div className="flex gap-1 mb-2 ml-12">
                    {[0, 6, 12, 18, 23].map(h => (
                      <div 
                        key={h} 
                        className="text-[10px] text-gray-400"
                        style={{ marginLeft: h === 0 ? 0 : `${(h - (h > 0 ? [0, 6, 12, 18][[ 0, 6, 12, 18].indexOf(h) - 1] || 0 : 0)) * 28 - 16}px` }}
                      >
                        {`${h}h`}
                      </div>
                    ))}
                  </div>
                  {/* Heatmap rows */}
                  {heatmap.heatmap.map((row, dayIndex) => (
                    <div key={dayIndex} className="flex items-center gap-1 mb-1">
                      <span className="w-10 text-xs text-gray-500">{heatmap.days[dayIndex]}</span>
                      {row.map((value, hourIndex) => (
                        <HeatmapCell 
                          key={hourIndex} 
                          value={value} 
                          max={heatmap.max_value} 
                        />
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-gray-400">
                Naviguez plus pour voir votre carte de chaleur
              </div>
            )}
          </div>
        </div>
      )}

      {/* Suggestions Tab */}
      {activeTab === 'suggestions' && (
        <div className="space-y-4">
          {suggestions.length > 0 ? (
            suggestions.map((suggestion, index) => (
              <div 
                key={index}
                className={`p-4 rounded-lg border-l-4 cursor-pointer hover:shadow-md transition-shadow ${getPriorityColor(suggestion.priority)}`}
                onClick={() => suggestion.path && navigate(suggestion.path)}
                data-testid={`suggestion-${index}`}
              >
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-white rounded-lg shadow-sm">
                    {getSuggestionIcon(suggestion.icon)}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">{suggestion.title}</h4>
                    <p className="text-sm text-gray-600 mt-1">{suggestion.description}</p>
                  </div>
                  {suggestion.path && (
                    <ChevronRight className="text-gray-400" size={20} />
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <Sparkles className="mx-auto text-gray-300 mb-4" size={48} />
              <h3 className="text-lg font-medium text-gray-600">Aucune suggestion pour le moment</h3>
              <p className="text-gray-400 mt-2">Continuez à utiliser l'application pour recevoir des recommandations personnalisées</p>
            </div>
          )}
        </div>
      )}

      {/* Badges Tab */}
      {activeTab === 'badges' && badges && (
        <div className="space-y-6">
          {/* XP Progress */}
          <div className="bg-gradient-to-r from-[#00D3A8] to-[#00BCD4] p-6 rounded-xl text-white">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-white/80 text-sm">Votre niveau</p>
                <p className="text-3xl font-bold">Niveau {1 + Math.floor((badges.total_xp || 0) / 100)}</p>
              </div>
              <div className="text-right">
                <p className="text-white/80 text-sm">Total XP</p>
                <p className="text-3xl font-bold">{badges.total_xp || 0}</p>
              </div>
            </div>
            <div className="bg-white/20 rounded-full h-3 overflow-hidden">
              <div 
                className="bg-white h-full rounded-full transition-all duration-500"
                style={{ width: `${((badges.total_xp || 0) % 100)}%` }}
              />
            </div>
            <p className="text-white/80 text-sm mt-2 text-center">
              {100 - ((badges.total_xp || 0) % 100)} XP pour le niveau suivant
            </p>
          </div>

          {/* Badges Grid */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Award size={20} />
              Badges de Navigation ({badges.earned_count}/{badges.total_count})
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {badges.badges?.map((badge, index) => (
                <div 
                  key={badge.id}
                  className={`relative p-4 rounded-xl border-2 text-center transition-all ${
                    badge.earned 
                      ? 'border-[#00D3A8] bg-white shadow-lg' 
                      : 'border-gray-200 bg-gray-50 opacity-60'
                  }`}
                  data-testid={`badge-${badge.id}`}
                >
                  {/* Level indicator */}
                  <div className={`absolute -top-2 -right-2 px-2 py-0.5 rounded-full text-[10px] font-bold text-white bg-gradient-to-r ${
                    LEVEL_COLORS[badge.level] || LEVEL_COLORS.bronze
                  }`}>
                    {badge.level}
                  </div>
                  
                  <div className={`text-4xl mb-2 ${!badge.earned && 'grayscale'}`}>
                    {badge.icon}
                  </div>
                  <h4 className="font-semibold text-sm text-gray-900">{badge.name}</h4>
                  <p className="text-xs text-gray-500 mt-1">{badge.description}</p>
                  <div className="flex items-center justify-center gap-1 mt-2 text-yellow-500">
                    <Zap size={12} />
                    <span className="text-xs font-bold">{badge.xp} XP</span>
                  </div>
                  {badge.earned && badge.earned_at && (
                    <p className="text-[10px] text-green-600 mt-1">
                      ✓ Obtenu le {new Date(badge.earned_at).toLocaleDateString('fr-FR')}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Leaderboard Tab */}
      {activeTab === 'leaderboard' && leaderboard && (
        <div className="space-y-6">
          {/* Current user rank */}
          <div className="bg-gradient-to-r from-yellow-400 to-orange-500 p-6 rounded-xl text-white">
            <div className="flex items-center gap-4">
              <div className="p-4 bg-white/20 rounded-full">
                <Trophy size={32} />
              </div>
              <div>
                <p className="text-white/80">Votre classement</p>
                <p className="text-4xl font-bold">#{leaderboard.current_user_rank || '-'}</p>
              </div>
            </div>
          </div>

          {/* Leaderboard Table */}
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <div className="p-4 border-b bg-gray-50">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Medal size={20} />
                Top Navigateurs
              </h3>
            </div>
            <div className="divide-y">
              {leaderboard.leaderboard?.map((entry, index) => (
                <div 
                  key={entry.user_id}
                  className={`flex items-center gap-4 p-4 ${
                    entry.is_current_user ? 'bg-[#00D3A8]/10' : ''
                  }`}
                >
                  {/* Rank */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                    index === 0 ? 'bg-yellow-400 text-white' :
                    index === 1 ? 'bg-gray-400 text-white' :
                    index === 2 ? 'bg-orange-400 text-white' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {entry.rank}
                  </div>
                  
                  {/* User info */}
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">
                      {entry.name}
                      {entry.is_current_user && <span className="ml-2 text-[#00D3A8]">(vous)</span>}
                    </p>
                    <p className="text-sm text-gray-500">Niveau {entry.level} • {entry.badges_count} badges</p>
                  </div>
                  
                  {/* XP */}
                  <div className="text-right">
                    <p className="font-bold text-[#00D3A8]">{entry.total_xp} XP</p>
                  </div>
                </div>
              ))}
              
              {(!leaderboard.leaderboard || leaderboard.leaderboard.length === 0) && (
                <div className="p-8 text-center text-gray-400">
                  <Trophy className="mx-auto mb-4 opacity-30" size={48} />
                  <p>Pas encore de classement disponible</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NavigationStats;
